import React, { useEffect, useState, useMemo } from 'react';
import sanitizeHtml from 'sanitize-html-react';
import { Badge, Button, Image, Popover, Spin, Tooltip } from 'antd-v5';
import { UserOutlined, FilePdfOutlined, LinkOutlined } from '@ant-design/icons';
import RolexInquiriesPopover from '../RolexInquiriesPopover';
import { listInquiries } from '../../../../../api/vendors';
import { OPEN_STATUSES } from '../../../Rolex/utils';
import './styles.scss';

const SPEC_SECTIONS = [
  {
    title: 'Model Case',
    rows: [
      { key: 'caseMaterial', label: 'Material' },
      { key: 'caseDiameter', label: 'Diameter' },
      { key: 'caseBezel', label: 'Bezel' },
      { key: 'caseCrystal', label: 'Crystal' },
      { key: 'caseback', label: 'Case Back' },
      { key: 'caseWindingCrown', label: 'Winding Crown' },
      { key: 'caseWaterResistance', label: 'Water Resistance' },
      { key: 'caseOysterArchitecture', label: 'Oyster Architecture' },
    ],
  },
  {
    title: 'Dial',
    rows: [{ key: 'dialTitle', label: 'Dial' }],
  },
  {
    title: 'Movement',
    rows: [
      { key: 'movementTitle', label: 'Type' },
      { key: 'movementCalibre', label: 'Calibre' },
      { key: 'powerReserve', label: 'Power Reserve' },
    ],
  },
  {
    title: 'Bracelet',
    rows: [
      { key: 'braceletTitle', label: 'Type' },
      { key: 'braceletClaspType', label: 'Clasp' },
    ],
  },
];

const RolexProductDetails = ({ loading, product, onOpenAddInquiry, inquiryRefreshKey = 0, preferredCurrency }) => {
  const [inquiryPopoverOpen, setInquiryPopoverOpen] = useState(false);
  const [inquiryCount, setInquiryCount] = useState(0);

  useEffect(() => {
    setInquiryCount(0);
    if (product?.id) {
      listInquiries({ vendorItemId: product.id, statuses: OPEN_STATUSES.join(',') })
        .then(response => {
          const total = response?.total ?? (Array.isArray(response?.data) ? response.data.length : 0);
          setInquiryCount(total);
        })
        .catch(() => setInquiryCount(0));
    }
  }, [product?.id, inquiryRefreshKey]);

  const attrs = product?.attributes || {};

  const heroImage = attrs.imageMain || product?.image || product?.images?.[0]?.url;

  const galleryImages = useMemo(() => {
    const gallery = Array.isArray(attrs.imageGallery) ? attrs.imageGallery : [];
    const main = heroImage ? [heroImage] : [];
    return [...main, ...gallery.filter(url => url !== heroImage)];
  }, [attrs.imageGallery, heroImage]);

  if (loading && !product) {
    return (
      <div className="rolex-details">
        <div className="rolex-details__loading">
          <Spin />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="rolex-details">
        <div className="rolex-details__empty">No Product Selected</div>
      </div>
    );
  }

  const collection = product.collection || attrs.collection || product.family;

  return (
    <div className="rolex-details">
      <div className="rolex-details__hero">
        <div className="rolex-details__hero-backdrop">
          {heroImage && <img className="rolex-details__hero-bg" src={heroImage} alt="" aria-hidden="true" />}
        </div>

        {collection && <div className="rolex-details__collection-badge">{collection}</div>}

        <div className="rolex-details__hero-actions">
          <Popover
            content={
              <RolexInquiriesPopover
                vendorItemId={product?.id}
                onAddInquiry={onOpenAddInquiry}
                onClose={() => setInquiryPopoverOpen(false)}
                refreshKey={inquiryRefreshKey}
              />
            }
            trigger="click"
            placement="bottomRight"
            open={inquiryPopoverOpen}
            onOpenChange={setInquiryPopoverOpen}
          >
            <Badge size="small" count={inquiryCount} color="#1677ff" offset={[-30, 4]}>
              <Tooltip title="Rolex Inquiries">
                <Button
                  className="rolex-details__hero-action-btn"
                  size="large"
                  icon={<UserOutlined style={{ color: '#006039' }} />}
                  disabled={loading}
                />
              </Tooltip>
            </Badge>
          </Popover>
        </div>

        {heroImage && <img className="rolex-details__hero-image" src={heroImage} alt={product.name} />}

        {galleryImages.length > 1 && (
          <Image.PreviewGroup>
            <div className="rolex-details__fan">
              {galleryImages.slice(0, 5).map(url => (
                <div key={url} className="rolex-details__fan-thumb">
                  <Image src={url} alt="" preview={{ mask: null }} />
                </div>
              ))}
            </div>
          </Image.PreviewGroup>
        )}

        {(attrs.link || attrs.brochureUrl) && (
          <div className="rolex-details__hero-links">
            {attrs.link && (
              <a
                className="rolex-details__hero-link"
                href={attrs.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <LinkOutlined /> View on Rolex.com
              </a>
            )}
            {attrs.brochureUrl && (
              <a
                className="rolex-details__hero-link"
                href={attrs.brochureUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FilePdfOutlined /> Download brochure
              </a>
            )}
          </div>
        )}
      </div>

      <div className="rolex-details__sheet">
        <div className="rolex-details__header-card">
          <div className="rolex-details__title-row">
            <div className="rolex-details__title">{product.name}</div>
            {(() => {
              const currencyEntry = preferredCurrency && attrs.prices?.[preferredCurrency];
              if (currencyEntry?.formattedPrice) {
                return <div className="rolex-details__price">{currencyEntry.formattedPrice}</div>;
              }
              const fallback = attrs.formattedPrice || product.msrp;
              if (!fallback) return null;
              return <div className="rolex-details__price">{String(fallback).replace(/\s*USD\s*$/, '')}</div>;
            })()}
          </div>

          <div className="rolex-details__meta">
            {product.mpn && <span>Ref. {product.mpn}</span>}
            {product.family && product.mpn && <span className="rolex-details__meta-divider">·</span>}
            {product.family && <span>{product.family}</span>}
          </div>

          {product.description && (
            <div
              className="rolex-details__description"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(product.description, { allowedTags: ['em', 'strong', 'br'] }),
              }}
            />
          )}
        </div>

        <div className="rolex-details__spec-wrap">
          {SPEC_SECTIONS.map(section => {
            const visibleRows = section.rows.filter(row => {
              const v = attrs[row.key];
              return v !== undefined && v !== null && v !== '';
            });
            if (visibleRows.length === 0) return null;

            return (
              <div key={section.title} className="rolex-details__spec-card">
                <div className="rolex-details__spec-title">{section.title}</div>
                <div className="rolex-details__spec-grid">
                  {visibleRows.map(row => (
                    <div
                      key={row.key}
                      className={`rolex-details__spec-row${row.full ? ' rolex-details__spec-row--full' : ''}`}
                    >
                      <span className="rolex-details__spec-label">{row.label}</span>
                      <span className="rolex-details__spec-value">{attrs[row.key]}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

        </div>
      </div>
    </div>
  );
};

export default RolexProductDetails;
